import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Modal, message, ConfigProvider, Divider, Select, DatePicker, Radio, notification } from "antd";
import moment from "moment";
import CustomInput from "../CustomInput";
import TextArea from "antd/es/input/TextArea";
import useUserStore from "../../stores/store";
import ApiService from "../../utils/ApiService";
import { staffPostions, validateEmergencyContact } from "../../services/mixed-service";
import { containsAlphabet } from "../../utils/Common";
import useScrollLock from "../../hooks/useScrollLock";
const staffPostionList = [{label: 'Select Position', value: ''}].concat(staffPostions);

function AddStaffModal({ openModal, records }) {
  const { user, branches, restaurantId, restaurantName, setNetworkCall } = useUserStore();
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [contact, setContact] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [branch, setBranch] = useState([]);
  const [address, setAddress] = useState("");
  const [gender, setGender] = useState("Male");
  const [dob, setDob] = useState("");
  const [error, setError] = useState("");
  const [messageApi, contextHolder] = message.useMessage();

  // Lock scroll when modal is open
  useScrollLock(openModal);
  
  const onSelectGender = (e) => {
    setGender(e.target.value);
  };

  const branchOptions = branches.map((branch) => ({
    value: branch.restaurantId, // Unique identifier
    label: `${branch.name}-${branch.branch}`, // Combine restaurant name and branch
  }));

  const handleBranchesChange = (value) => setBranch(value);

  const clearFields = () => {
    setName("");
    setPosition("");
    setContact("");
    setEmergencyContact("");
    setEmergencyContactName("");
    setAddress("");
    setGender("Male");
    setDob(null); // Ensure DOB is cleared
    setBranch([]);
    setError("");
  };

  useEffect(() => {
    if (openModal) {
      clearFields();
    }
  }, [openModal]);

  const handleOk = async () => {
    console.log("handleOk called");
    console.log({
      name,
      position,
      dob,
      contact,
      emergencyContact,
      emergencyContactName,
      gender,
      branchIds: branch,
      address,
    });
    // Validation checks
    if (!name) {
      setError("Name required");
      return;
    }
    if (!position || position === "Select Position") {
      setError("Position required");
      return;
    }
    if (!dob) {
      setError("Date of Birth required");
      return;
    }

    if (contact.length !== 11) {
      if (contact.length == 0) {
        messageApi.error("Phone number is empty");
        return;
      }
      messageApi.error("Phone number is less than 11 digit");
      return;
    }

    if (!gender) {
      setError("Gender is required");
      return;
    }

    if (emergencyContactName !== "") {
      const trimmedName = emergencyContactName.trim();

      if (trimmedName.length < 2) {
        setError("Emergency contact name must be at least 2 characters long.");
        return;
      }

      if (!containsAlphabet(trimmedName)) {
        setError("The emergency contact name cannot contain only numbers.");
        return;
      }
    }
    
    const emergencyContactErr = validateEmergencyContact(emergencyContact);

    if (emergencyContactErr) {
      messageApi.error(emergencyContactErr);
      return;
    }

    if (branch.length === 0) {
      setError("At least one branch must be selected");
      return;
    }

    // Prepare API request payload
    const param = {
      name,
      position,
      dob,
      contact,
      emergencyContact,
      emergencyContactName,
      gender,
      branchIds: branch,
      address,
    };

    try {
      // Make the API call
      setNetworkCall(true);
      const res = await ApiService.ADD_STAFF(
        {
          restaurantId,
          name,
          position,
          dob,
          countryCode: "880", // Static country code (can be changed based on your requirement)
          phone: contact,
          gender,
          emergencyContactName,
          emergencyContactNumber: emergencyContact,
          branchIds: branch,
          address,
        },
        user.token.token
      );

      // Handle response and pass param if successful
      if (res.code === 200) {
        const selectedBranches = branches.filter((b) => branch.includes(b.restaurantId));
        const otherBranches = selectedBranches.filter((b) => !b.name.includes(restaurantName));

        if (otherBranches.length > 0) {
          const description = `${name} has been added successfully.`;

          messageApi.success(description);
        }

        records.onFinish({ showModal: false, param });
        clearFields(); // Clear all fields after successful save
      } else {
        messageApi.error(res.message);
      }
    } catch (error) {
      console.error("Error while adding staff:", error);
      messageApi.success("Something went wrong. Please, try again.");
    } finally {
      setNetworkCall(false);
    }
  };

  const handleCancel = () => {
    records.onCancel();
  };

  const onSelectDate = (date, dateString) => {
    // Convert the dateString to a Moment object
    const selectedDate = moment(dateString, "YYYY-MM-DD");
    const today = moment().startOf("day"); // Get today's date at midnight

    // Compare the selected date with today's date
    if (selectedDate.isSameOrAfter(today)) {
      messageApi.error("Date of Birth cannot be today or a future date");
      setDob(""); // Clear the input if invalid date is selected
    } else {
      setDob(dateString); // Only set the dob if it's a valid past date
    }
  };

  const updatePhone = (val) => {
    const isValid = /^\d{0,11}$/.test(val); // Allow up to 11 digits
    if (isValid) {
      setContact(val);
    }
  };

  const handleEmergencyContactChange = (val) => {
    if (/^\d{0,11}$/.test(val)) {
      // Allow up to 11 digits
      setEmergencyContact(val);
    }
  };

  function onHandleStaffName(val) {
    if (val.length > 100) {
      message.warning("Staff name character limit exceeds. Please make it upto 100 characters.");
      return;
    }
    setName(val);
  }

  function onHandleAddress(e) {
    const address = e.target.value;
    if (address.length > 200) {
      message.warning("Address character limit exceeds. Please make it upto 200 characters.");
      return;
    }
    setAddress(address);
  }

  function onHandleEmergencyContactName(val) {
    if (val.length > 100) {
      message.warning("Emergency contact name character limit exceeds. Please make it upto 100 characters.");
      return;
    }
    setEmergencyContactName(val);
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: "#FFD700",
            defaultHoverColor: "#000000",
            fontFamily: "ubold",
          },
        },
      }}
    >
      <Modal
        maskClosable={false}
        open={openModal}
        okButtonProps={{
          style: {
            backgroundColor: "#FFD700",
            color: "#000000",
            fontFamily: "ubold",
          },
        }}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        {contextHolder}
        <div className="flex flex-col font-uregular">
          <div className="text-2xl font-ubold">Add Staff</div>
          <Divider />

          {error && <div className="w-full text-start mb-[8px] text-pink font-ubold">{error}</div>}
          {/* Name */}
          <CustomInput
            value={name}
            setValue={onHandleStaffName}
            showPadding={false}
            label="Staff Name"
            required={true}
            placeholder="e.g. Kareem or Heera"
          />

          {/*Position and Gender*/}
          <div className="flex flex-row justify-between">
            <div className="w-1/2 mr-2">
              <div className="ml-1 text-sm font-uregular flex items-center">
                <span>Position</span>
                <span className="font-ubold text-red-700 ml-1">*</span>
              </div>

              <Select
                defaultValue={position}
                value={position}
                className="w-full"
                onChange={setPosition}
                options={staffPostionList}
              />
            </div>
            <div className="w-1/2 ml-[4px]">
              <div className="flex flex-col mb-[12px]">
                <div className="ml-[4px] text-sm font-uregular flex flex-row">
                  <div>DOB</div>
                  <div className="font-ubold text-red-700 ml-[2px]">*</div>
                </div>
                {/* <DatePicker onChange={onSelectDate} /> */}
                <DatePicker
                  onChange={onSelectDate}
                  className={"h-[30px] w-full focus:outline-2 focus:outline-offset-2 focus:black"}
                  value={dob ? dayjs(dob) : null}
                  disabledDate={(current) => {
                    // Disable today and future dates
                    return current && current >= dayjs().startOf("day");
                  }}
                />
              </div>
            </div>
          </div>

          {/* Contact and DOB */}
          <div className="flex flex-row justify-between">
            <div className="w-1/2 mr-[4px]">
              <CustomInput
                value={contact}
                setValue={updatePhone}
                showPadding={false}
                label="Phone"
                required={true}
                placeholder="e.g. 017XXXXXXXX"
              />
            </div>
            <div className="w-1/2 ml-[4px]">
              <div className="flex flex-col mb-[12px]">
                <div className="ml-[4px] text-sm font-uregular flex flex-row">
                  <div>Gender</div>
                  <div className="font-ubold text-red-700 ml-[2px]">*</div>
                </div>
                <Radio.Group onChange={onSelectGender} value={gender} className="mt-[4px]">
                  <Radio value={"Male"}>Male</Radio>
                  <Radio value={"Female"}>Female</Radio>
                </Radio.Group>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="flex flex-row justify-between">
            <div className="w-1/2 mr-[4px]">
              <CustomInput
                value={emergencyContactName}
                setValue={onHandleEmergencyContactName}
                showPadding={false}
                label="Emergency Contact Name"
                required={false}
                placeholder="e.g. Rashed"
              />
            </div>
            <div className="w-1/2 ml-[4px]">
              <CustomInput
                value={emergencyContact}
                setValue={handleEmergencyContactChange}
                showPadding={false}
                label="Emergency Contact Number"
                required={false}
                placeholder="e.g. 017XXXXXXXX"
              />
            </div>
          </div>

          {/* Branch Field */}
          <div className="flex flex-col mb-[12px]">
            <div className="ml-[4px] text-sm font-uregular flex flex-row">
              <div>Branch</div>
              <div className="font-ubold text-red-700 ml-[2px]">*</div>
            </div>
            <Select
              mode="multiple"
              className="font-uregular"
              showSearch
              onChange={handleBranchesChange}
              placeholder="Select Branch"
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              options={branchOptions}
              value={branch}
            />
          </div>

          {/* Address */}
          <div className="flex flex-col mb-[12px]">
            <div className="ml-[4px] text-sm font-uregular flex flex-row">
              <div>Address</div>
            </div>
            <TextArea rows={2} placeholder="Max 200 words" value={address} onChange={onHandleAddress} />
          </div>
          <Divider />
        </div>
      </Modal>
    </ConfigProvider>
  );
}

export default AddStaffModal;
